import * as THREE from 'three';
import { SAFE_ZONE_VERTEX_CHUNK, safeZoneUniforms } from '../../shaders/safeZone.glsl.js';

// THE PEOPLE ON STAGE: a DJ behind the console and two to four dancers on
// the podiums. Each one is a 2D skeleton (13 joints, metres, feet at the
// origin) posed on the CPU every frame and drawn as a silhouette by distance
// in a camera-facing quad (R1, R13): a black body, a rim of light on the
// edges that face the screens behind them, nothing in pixels. The same
// distance function draws the DJ on the LED screens in the "camera" mode.
//
// Joints: 0 pelvis, 1 chest, 2 head, 3 shoulder L, 4 elbow L, 5 hand L,
// 6 shoulder R, 7 elbow R, 8 hand R, 9 knee L, 10 foot L, 11 knee R, 12 foot R.

export const JOINTS = 13;
export const FIG_W = 2.8, FIG_H = 2.7;      // the quad, metres

// --- the distance function (GLSL), shared with the screens --------------------------
export const FIGURE_SDF_CHUNK = `
  vec3 fCaps(vec2 p, vec2 a, vec2 b, float ra, float rb) {
    vec2 pa = p - a, ba = b - a;
    float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-6), 0.0, 1.0);
    vec2 q = pa - ba * h;
    float l = max(length(q), 1e-5);
    return vec3(l - mix(ra, rb, h), q / l);
  }
  vec3 fCirc(vec2 p, vec2 c, vec2 r) {
    vec2 q = (p - c) / r;
    float l = max(length(q), 1e-5);
    return vec3((l - 1.0) * min(r.x, r.y), normalize(q / r));
  }
  vec3 fU(vec3 a, vec3 b) { return a.x < b.x ? a : b; }
  // J: the joints; look: x hair (0 short, 1 ponytail, 2 long, 3 bun, 4 cap), y headphones, z profile (0..1, signed side), w build (0 slim .. 1 broad)
  vec3 figureSDF(vec2 p, vec2 J[${JOINTS}], vec4 look) {
    float br = 1.0 + 0.18 * look.w;
    vec2 hipL = J[0] + vec2(-0.1 * br, 0.0), hipR = J[0] + vec2(0.1 * br, 0.0);
    vec3 d = fCaps(p, hipL, J[9], 0.085 * br, 0.065);
    d = fU(d, fCaps(p, J[9], J[10], 0.062, 0.05));
    d = fU(d, fCaps(p, hipR, J[11], 0.085 * br, 0.065));
    d = fU(d, fCaps(p, J[11], J[12], 0.062, 0.05));
    d = fU(d, fCaps(p, J[10] + vec2(-0.03, 0.03), J[10] + vec2(0.07, 0.03), 0.045, 0.04));
    d = fU(d, fCaps(p, J[12] + vec2(-0.03, 0.03), J[12] + vec2(0.07, 0.03), 0.045, 0.04));
    d = fU(d, fCaps(p, J[0], J[1] + vec2(0.0, -0.08), 0.15 * br, 0.17 * br));
    d = fU(d, fCaps(p, J[3], J[6], 0.085 * br, 0.085 * br));
    d = fU(d, fCaps(p, J[1], J[2] + vec2(0.0, -0.08), 0.055, 0.05));
    d = fU(d, fCaps(p, J[3], J[4], 0.058 * br, 0.05));
    d = fU(d, fCaps(p, J[4], J[5], 0.048, 0.04));
    d = fU(d, fCirc(p, J[5], vec2(0.05)));
    d = fU(d, fCaps(p, J[6], J[7], 0.058 * br, 0.05));
    d = fU(d, fCaps(p, J[7], J[8], 0.048, 0.04));
    d = fU(d, fCirc(p, J[8], vec2(0.05)));
    vec2 hc = J[2];
    d = fU(d, fCirc(p, hc, vec2(0.105, 0.125)));
    float side = look.z;
    // the profile: a nose and a chin on the facing side
    if (abs(side) > 0.3) d = fU(d, fCirc(p, hc + vec2(sign(side) * 0.1, -0.015), vec2(0.035, 0.028)));
    // hair
    if (look.x > 0.5 && look.x < 1.5) d = fU(d, fCaps(p, hc + vec2(-sign(side + 1e-3) * 0.09, 0.05), hc + vec2(-sign(side + 1e-3) * 0.16, -0.16), 0.045, 0.03));
    else if (look.x > 1.5 && look.x < 2.5) d = fU(d, fCaps(p, hc + vec2(0.0, 0.04), hc + vec2(0.0, -0.22), 0.13, 0.12));
    else if (look.x > 2.5 && look.x < 3.5) d = fU(d, fCirc(p, hc + vec2(0.0, 0.14), vec2(0.06)));
    else if (look.x > 3.5) d = fU(d, fCaps(p, hc + vec2(-0.11, 0.06), hc + vec2(0.11 + 0.08 * sign(side + 1e-3), 0.06), 0.035, 0.03));
    // headphones: the band over the head and two cups
    if (look.y > 0.5) {
      vec2 q = p - hc;
      if (q.y > -0.02) { float l = length(q); vec3 band = vec3(abs(l - 0.15) - 0.018, normalize(q) * sign(l - 0.15)); d = fU(d, band); }
      d = fU(d, fCirc(p, hc + vec2(-0.125, -0.01), vec2(0.04, 0.055)));
      d = fU(d, fCirc(p, hc + vec2(0.125, -0.01), vec2(0.04, 0.055)));
    }
    return d;
  }
`;

const figureVertex = `
  precision highp float;
  uniform vec3 uBase;            // feet, world
  uniform float uScale;
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying vec2 vP;
  varying float vSafe;
  void main() {
    vec3 toCam = cameraPosition - uBase; toCam.y = 0.0;
    vec3 fwd = length(toCam) > 1e-4 ? normalize(toCam) : vec3(0.0, 0.0, 1.0);
    vec3 right = normalize(cross(vec3(0.0, 1.0, 0.0), fwd));
    vec2 local = vec2(position.x * ${FIG_W.toFixed(2)}, (position.y + 0.5) * ${FIG_H.toFixed(2)});
    vP = local;
    vec3 w = uBase + (right * local.x + vec3(0.0, local.y, 0.0)) * uScale;
    gl_Position = projectionMatrix * viewMatrix * vec4(w, 1.0);
    vSafe = safeZoneAttenuation(gl_Position);
  }
`;
const figureFragment = `
  precision highp float;
  uniform vec2 uJ[${JOINTS}];
  uniform vec4 uLook;
  uniform sampler2D uZones;      // the screens' light, 8 zones across
  uniform float uZoneU;          // which zone lights this figure from behind
  uniform vec3 uSpot;            // a projector on this figure (colour x level)
  uniform float uRimGain;
  uniform float uLeds;           // (the DJ) the console's glow on the hands and the face from below
  uniform vec3 uLedCol;
  varying vec2 vP;
  varying float vSafe;
  ${FIGURE_SDF_CHUNK}
  void main() {
    vec3 d = figureSDF(vP, uJ, uLook);
    float px = max(length(fwidth(vP)), 1e-4);
    float cov = clamp(0.5 - d.x / px, 0.0, 1.0);
    if (cov <= 0.0) discard;
    // the rim: the outer 1.5-3 px of the silhouette, strongest where the edge faces up and out (the screens are behind and above)
    float band = 1.0 - smoothstep(0.0, 2.6 * px, -d.x);
    vec3 back = texture2D(uZones, vec2(uZoneU, 0.5)).rgb;
    // the edges that face up and out toward the screens; never a full outline (R13)
    float face = clamp(d.z * 0.9 + abs(d.y) * 0.3 - 0.1, 0.0, 1.0);
    vec3 col = vec3(0.004, 0.005, 0.009);
    col += (back * 1.6 * uRimGain + uSpot) * band * face;
    col += uSpot * 0.08;
    // the console's diodes light the hands and the chin from below, faintly
    col += uLedCol * uLeds * 0.5 * band * clamp(-d.z, 0.0, 1.0);
    gl_FragColor = vec4(col * vSafe, cov);
  }
`;

// --- poses ----------------------------------------------------------------------------
const UPPER = 0.29, FORE = 0.27;
function ik(sx, sy, tx, ty, bend, out, o) {
  let dx = tx - sx, dy = ty - sy, L = Math.hypot(dx, dy);
  const maxL = UPPER + FORE - 1e-3;
  if (L > maxL) { tx = sx + dx / L * maxL; ty = sy + dy / L * maxL; dx = tx - sx; dy = ty - sy; L = maxL; }
  L = Math.max(L, 1e-3);
  const a = Math.acos(Math.max(-1, Math.min(1, (UPPER * UPPER + L * L - FORE * FORE) / (2 * UPPER * L))));
  const base = Math.atan2(dy, dx) + bend * a;
  out[o] = sx + Math.cos(base) * UPPER; out[o + 1] = sy + Math.sin(base) * UPPER;
  out[o + 2] = tx; out[o + 3] = ty;
}
const ease = (x) => { x = Math.max(0, Math.min(1, x)); return x * x * (3 - 2 * x); };

// m: { beats (float, the beat count), beatPhase, bar (bar phase 0..1), barIndex,
//      armUp (0..1, one arm), armsUp (0..1, both), jump (metres), nod (0..1 amount), t }
export function poseDJ(J, m, profile) {
  const bp = m.beatPhase, nod = Math.exp(-bp * 6) * m.nod;
  const bounce = 0.03 * (1 - Math.cos(bp * Math.PI * 2)) * 0.5 * m.nod;
  const y0 = m.jump - bounce;
  const px = profile;                                  // 0 front .. 1 side (squash in x)
  const sq = 1 - 0.75 * Math.abs(px);
  J[20] = -0.17 * sq; J[21] = y0 * 0; J[24] = 0.17 * sq; J[25] = 0;          // feet stay on the floor unless jumping
  if (m.jump > 0.001) { J[21] = m.jump; J[25] = m.jump; }
  J[18] = -0.15 * sq; J[19] = 0.48 + y0 * 0.6; J[22] = 0.15 * sq; J[23] = 0.48 + y0 * 0.6;
  J[0] = 0; J[1] = 0.95 + y0;
  J[2] = 0.02 * px; J[3] = 1.42 + y0 - 0.015 * nod;
  J[4] = 0.05 * px + 0.01 * Math.sin(m.t * 0.7); J[5] = 1.65 + y0 - 0.045 * nod;
  const sh = 0.2 * sq;
  J[6] = -sh; J[7] = 1.42 + y0 - 0.015 * nod; J[12] = sh; J[13] = 1.42 + y0 - 0.015 * nod;
  // hands on the console (its top ~1.08 m), working the decks; one to the headphone every fourth bar
  const work = Math.sin(m.beats * Math.PI * 0.5) * 0.08;
  let lx = -0.42 * sq + work + 0.25 * px, ly = 1.1 + 0.02 * Math.sin(m.t * 3.1);
  let rx = 0.42 * sq - 0.05 * Math.sin(m.beats * Math.PI) + 0.25 * px, ry = 1.1 + 0.02 * Math.cos(m.t * 2.7);
  if (Math.floor(m.barIndex) % 4 === 3 && m.armUp < 0.2) { lx = -0.14 * sq; ly = 1.62 + y0; }
  // one arm up on the rise, both on the drop
  const up1 = Math.max(m.armUp, m.armsUp), up2 = m.armsUp;
  rx += (0.38 - rx) * up1; ry += (2.28 + y0 - ry) * up1;
  lx += (-0.38 - lx) * up2; ly += (2.28 + y0 - ly) * up2;
  ik(J[12], J[13], rx, ry + y0 * (1 - up1), -1, J, 14);
  ik(J[6], J[7], lx, ly + y0 * (1 - up2), 1, J, 8);
}

// styles: 0 sway, 1 arms, 2 turn, 3 wave
export function poseDancer(J, m, style, seed) {
  const b = m.beats + seed * 0.37, bp = b % 1, bar = (b / 4) % 1;
  const bounce = 0.05 * Math.abs(Math.sin(b * Math.PI));
  let sq = 1, sway = 0, prof = 0;
  if (style === 2) { const a = bar * Math.PI * 2; sq = Math.max(0.25, Math.abs(Math.cos(a))); prof = Math.sin(a); }
  if (style === 0 || style === 3) sway = 0.09 * Math.sin(b * Math.PI);
  const y0 = -bounce + m.jump;
  J[0] = sway; J[1] = 0.95 + y0;
  J[2] = sway * 1.3; J[3] = 1.42 + y0;
  J[4] = sway * 1.5 + 0.03 * Math.sin(b * Math.PI * 2); J[5] = 1.65 + y0;
  J[6] = J[2] - 0.2 * sq; J[7] = J[3]; J[12] = J[2] + 0.2 * sq; J[13] = J[3];
  const kneeOut = 0.06 * Math.abs(Math.sin(b * Math.PI));
  J[20] = -0.2 * sq; J[21] = Math.max(0, m.jump); J[24] = 0.2 * sq; J[25] = Math.max(0, m.jump);
  J[18] = -0.17 * sq - kneeOut + sway * 0.5; J[19] = 0.48 + y0 * 0.6; J[22] = 0.17 * sq + kneeOut + sway * 0.5; J[23] = 0.48 + y0 * 0.6;
  let lx, ly, rx, ry;
  if (style === 1) {                                   // arms in turn, up on the beat
    const l = Math.floor(b) % 2 === 0, k = ease(1 - bp * 1.6);
    lx = J[6] - 0.3; ly = 1.2 + y0 + (l ? 1.0 * k : 0.1); rx = J[12] + 0.3; ry = 1.2 + y0 + (!l ? 1.0 * k : 0.1);
  } else if (style === 2) {                            // arms out through the turn
    lx = J[6] - 0.5 * sq; ly = 1.5 + y0 + 0.2 * Math.sin(b * Math.PI); rx = J[12] + 0.5 * sq; ry = 1.5 + y0 - 0.2 * Math.sin(b * Math.PI);
  } else if (style === 3) {                            // a wave from one hand to the other
    const w = Math.sin(b * Math.PI * 0.5);
    lx = J[6] - 0.45; ly = 1.55 + y0 + 0.35 * w; rx = J[12] + 0.45; ry = 1.55 + y0 - 0.35 * w;
  } else {                                             // sway: hands low, swinging
    lx = J[6] - 0.15 + 0.15 * Math.sin(b * Math.PI); ly = 0.95 + y0 + 0.2 * Math.abs(Math.cos(b * Math.PI));
    rx = J[12] + 0.15 + 0.15 * Math.sin(b * Math.PI); ry = 0.95 + y0 + 0.2 * Math.abs(Math.sin(b * Math.PI));
  }
  // everyone's arms up on the drop
  lx += (J[6] - 0.3 - lx) * m.armsUp; ly += (2.3 + y0 - ly) * m.armsUp;
  rx += (J[12] + 0.3 - rx) * m.armsUp; ry += (2.3 + y0 - ry) * m.armsUp;
  ik(J[6], J[7], lx, ly, 1, J, 8);
  ik(J[12], J[13], rx, ry, -1, J, 14);
  return prof;
}

// --- the layer --------------------------------------------------------------------------
export class StageFigures {
  // specs: [{ base: Vector3, kind: 'dj'|'dancer', style, look: [hair, phones, 0, build], zoneU, scale }]
  constructor(scene, specs, zonesTexture) {
    this._scene = scene;
    this._figs = [];
    const geo = new THREE.PlaneGeometry(1, 1);
    this._geo = geo;
    specs.forEach((s, i) => {
      const J = new Float32Array(JOINTS * 2);
      const jv = []; for (let k = 0; k < JOINTS; k++) jv.push(new THREE.Vector2());
      const u = Object.assign({}, safeZoneUniforms, {
        uBase: { value: s.base.clone() }, uScale: { value: s.scale || 1 }, uJ: { value: jv },
        uLook: { value: new THREE.Vector4(s.look[0], s.look[1], 0, s.look[3]) }, uZones: { value: zonesTexture }, uZoneU: { value: s.zoneU },
        uSpot: { value: new THREE.Vector3() }, uRimGain: { value: 1 }, uLeds: { value: s.kind === 'dj' ? 1 : 0 }, uLedCol: { value: new THREE.Vector3(0.2, 0.5, 1) }
      });
      const mat = new THREE.ShaderMaterial({ uniforms: u, vertexShader: figureVertex, fragmentShader: figureFragment, transparent: true, depthWrite: true, depthTest: true, side: THREE.DoubleSide, extensions: { derivatives: true } });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.frustumCulled = false;
      mesh.renderOrder = 20;
      scene.add(mesh);
      this._figs.push({ spec: s, J, u, mesh, seed: i * 1.7 });
    });
  }
  get dj() { return this._figs.find((f) => f.spec.kind === 'dj'); }
  // m: music state (see poseDJ); profileSide: -1..1 for the DJ (the side shot), spot colours per figure
  update(m, profileSide, spots) {
    for (const f of this._figs) {
      let prof = 0;
      if (f.spec.kind === 'dj') { poseDJ(f.J, m, profileSide); prof = profileSide; }
      else prof = poseDancer(f.J, m, f.spec.style, f.seed);
      for (let k = 0; k < JOINTS; k++) f.u.uJ.value[k].set(f.J[k * 2], f.J[k * 2 + 1]);
      f.u.uLook.value.z = prof;
      const sp = spots && spots[this._figs.indexOf(f)];
      if (sp) f.u.uSpot.value.copy(sp); else f.u.uSpot.value.set(0, 0, 0);
    }
  }
  setVisible(v) { for (const f of this._figs) f.mesh.visible = v; }
  dispose() {
    for (const f of this._figs) { this._scene.remove(f.mesh); f.mesh.material.dispose(); }
    this._geo.dispose();
    this._figs = [];
  }
}
